// components/CreateEventTicketModal.js

import React, { useCallback, useEffect, useState } from "react";
import Modal from "./Modal";
import styled from "styled-components";
import Button from "../Button/Button";
import InputText from "../Input/InputText";
import Switch from "../Switch";
import Typography from "../Typography";
import ToogableTabs from "../Toogle/ToogableTabs";
import { theme } from "../../theme";
import axiosClient from "../../api/axiosClient";
import StripeOnboardingModal from "./StripeOnboardingModal";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { TICKET_FREE, TICKET_PAID } from "../../constants/eventTypes";
import Tooltip from "../Tooltip/Tooltip";
import InfoCircleOutlineIcon from "../Icons/InfoCircleOutlineIcon";

const CreateEventTicketModal = ({
	ticketName,
	ticketPrice,
	ticketType,
	capacity,
	approvalRequired,
	onSave,
	onClose,
}) => {
	const { t } = useTranslation(["createEvent", "eventCategories"]);
	const {
		user,
		stripeStatus,
		refreshStripeStatus: refreshAuthStripeStatus,
	} = useAuth();
	const userId = user?.id;

	// Estados para Stripe
	const [hasStripeAccount, setHasStripeAccount] = useState(false);
	const [chargesEnabled, setChargesEnabled] = useState(false);
	const [loadingStripeCheck, setLoadingStripeCheck] = useState(false);
	const canUsePaid = hasStripeAccount && chargesEnabled;

	const [selectedTab, setSelectedTab] = useState(ticketType);
	const [localName, setLocalName] = useState(ticketName);
	const [localPrice, setLocalPrice] = useState(ticketPrice);
	const [localCapacity, setLocalCapacity] = useState(capacity);
	const [localApprovalRequired, setLocalApprovalRequired] =
		useState(approvalRequired);

	// Control del modal de onboarding
	const [showOnboardingModal, setShowOnboardingModal] = useState(false);

	// Opciones para ToogableTabs
	// const ticketOptions = [
	//   { label: t('ticket.free', { ns: 'eventCategories' }), value: 'free' },
	//   {
	//     label: t('ticket.paid', { ns: 'eventCategories' }),
	//     value: 'paid',
	//     disabled: !canUsePaid
	//   }
	// ];

	// Opciones para ToogableTabs
	const ticketOptions = [
		{ label: t("ticket.free", { ns: "eventCategories" }), value: TICKET_FREE },
		{ label: t("ticket.paid", { ns: "eventCategories" }), value: TICKET_PAID },
	];

	const refreshStripeStatus = useCallback(async () => {
		if (!userId) return;
		setLoadingStripeCheck(true);
		try {
			const status = refreshAuthStripeStatus
				? await refreshAuthStripeStatus(userId)
				: null;

			if (status) {
				setHasStripeAccount(status.hasStripeAccount);
				setChargesEnabled(status.chargesEnabled);
			} else {
				const resp = await axiosClient.get(
					`/api/stripe/refresh-account-status?userId=${userId}`,
				);
				setHasStripeAccount(resp.data.hasStripeAccount);
				setChargesEnabled(resp.data.chargesEnabled);
			}
		} catch (error) {
			console.error("Error verificando estado de Stripe:", error);
		} finally {
			setLoadingStripeCheck(false);
		}
	}, [userId, refreshAuthStripeStatus]);

	// Al montar (o cuando el userId esté disponible), se consulta Stripe para obtener el estado de la cuenta.
	useEffect(() => {
		if (userId) {
			refreshStripeStatus();
		}
	}, [userId, refreshStripeStatus]);

	// Escuchar cambios en el estado de Stripe del contexto de autenticación
	useEffect(() => {
		setHasStripeAccount(!!stripeStatus.hasStripeAccount);
		setChargesEnabled(!!stripeStatus.chargesEnabled);
	}, [stripeStatus.hasStripeAccount, stripeStatus.chargesEnabled]);

	// Manejar el cierre del modal de onboarding
	const handleCloseOnboarding = async () => {
		setShowOnboardingModal(false);
		// Re-check status (por si terminó el onboarding con éxito)
		await refreshStripeStatus();
	};

	// useEffect para setear precio por defecto cuando es pago (tu lógica anterior)
	useEffect(() => {
		if (
			selectedTab === TICKET_PAID &&
			(localPrice === "" || Number(localPrice) === 0)
		) {
			setLocalPrice(10);
		} else if (selectedTab === TICKET_FREE) {
			setLocalPrice(0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTab]);

	// Sincronizar con el prop ticketType
	useEffect(() => {
		setSelectedTab(ticketType);
		setLocalName(ticketName);
		setLocalPrice(ticketPrice);
		setLocalCapacity(capacity);
		setLocalApprovalRequired(approvalRequired);
	}, [ticketType, ticketName, ticketPrice, capacity, approvalRequired]);

	return (
		<Modal
			onClose={onClose}
			title={t("createEventTicketModal.title")}
			closeOnOverlayClick={false}
		>
			<ModalContent>
				<ModalContentRow>
					<Typography
						as="p"
						$variant="body-1-medium"
						color={theme.colors.defaultWeak}
					>
						{t("createEventTicketModal.description")}
					</Typography>
				</ModalContentRow>
				<ModalContentRow>
					<InputWrapper>
						<Typography
							$variant="body-2-medium"
							as="label"
							color={theme.colors.defaultMain}
						>
							{t("createEventTicketModal.name.label")}
						</Typography>
						<InputText
							$size="large"
							type="text"
							value={localName}
							onChange={(e) => setLocalName(e.target.value)}
							placeholder={t("createEventTicketModal.name.placeholder")}
							required
						/>
					</InputWrapper>
				</ModalContentRow>

				<ModalContentRow>
					{/* <ToogableTabs
            options={ticketOptions}
            activeOption={ticketType}
            onTabChange={(value) => setTicketType(value)}
          /> */}

					<ToogableTabs
						options={ticketOptions}
						activeOption={selectedTab}
						onTabChange={(value) => {
							if (value === TICKET_PAID && !canUsePaid) {
								setSelectedTab(TICKET_PAID); // Show paid tab content
							} else {
								setSelectedTab(value); // Update visual tab
							}
						}}
					/>
				</ModalContentRow>

				{selectedTab === TICKET_FREE && (
					<>
						<ModalContentRow>
							<InputWrapperHorizontal>
								<InputLabelWrapper>
									<Typography
										$variant="body-1-medium"
										as="label"
										color={theme.colors.defaultMain}
									>
										{t("createEventTicketModal.freeTicket.capacityLabel")}
									</Typography>
									<Tooltip
										text={t(
											"createEventTicketModal.freeTicket.capacityTooltip",
										)}
										maxWidth="220px"
										style={{ marginLeft: "-4px" }}
									>
										<InfoButton>
											<InfoCircleOutlineIcon
												width="20px"
												height="20px"
												fill={theme.colors.defaultSubtle}
											/>
										</InfoButton>
									</Tooltip>
								</InputLabelWrapper>
								<CapacityInput
									size="medium"
									type="number"
									value={localCapacity}
									onChange={(e) => setLocalCapacity(e.target.value)}
									placeholder={t(
										"createEventTicketModal.freeTicket.capacityPlaceholder",
									)}
									required
								/>
							</InputWrapperHorizontal>
						</ModalContentRow>
						<ModalContentRow>
							<InputWrapperHorizontal style={{ minHeight: "38px" }}>
								<InputLabelWrapper>
									<Typography
										$variant="body-1-medium"
										as="label"
										color={theme.colors.defaultMain}
									>
										{t("createEventTicketModal.freeTicket.approvalLabel")}
									</Typography>
									<Tooltip
										text={t(
											"createEventTicketModal.freeTicket.approvalTooltip",
										)}
										maxWidth="200px"
										style={{ marginLeft: "-4px" }}
									>
										<InfoButton>
											<InfoCircleOutlineIcon
												width="20px"
												height="20px"
												fill={theme.colors.defaultSubtle}
											/>
										</InfoButton>
									</Tooltip>
								</InputLabelWrapper>
								<Switch
									value={localApprovalRequired}
									ariaLabel={t(
										"createEventTicketModal.freeTicket.approvalLabel",
									)}
									onChange={(value) => setLocalApprovalRequired(value)}
									disabled={selectedTab === TICKET_PAID}
								/>
							</InputWrapperHorizontal>
						</ModalContentRow>
					</>
				)}

				{selectedTab === TICKET_PAID && (
					<>
						{loadingStripeCheck ? (
							<p>{t("createEventTicketModal.paidTicket.loadingStripe")}</p>
						) : (
							<>
								{/* Caso 1: El usuario no tiene cuenta Stripe conectada */}
								{!hasStripeAccount && (
									<ConnectedAccountWrapper>
										<ConnectedAccountBanner>
											<IntegrationIcon
												src="/icons/motorik-stripe-connect.svg"
												alt="Stripe Integration"
											/>
											<Typography as="h3" $variant="title-5-semibold">
												{t("createEventTicketModal.paidTicket.noStripe.title")}
											</Typography>
											<Typography
												as="p"
												$variant="body-1-regular"
												color={theme.colors.defaultWeak}
											>
												{t(
													"createEventTicketModal.paidTicket.noStripe.description",
												)}
											</Typography>
											<StripeActions>
												<a
													href={"/user/" + userId + "/settings"}
													target="_blank"
													rel="noopener noreferrer"
												>
													<Button size="medium">
														{t(
															"createEventTicketModal.paidTicket.noStripe.button",
														)}
													</Button>
												</a>
											</StripeActions>
										</ConnectedAccountBanner>
									</ConnectedAccountWrapper>
								)}

								{/* Caso 2: El usuario tiene la cuenta conectada pero los cobros no están habilitados */}
								{hasStripeAccount && !chargesEnabled && (
									<ConnectedAccountWrapper>
										<ConnectedAccountBanner>
											<IntegrationIcon
												src="/icons/motorik-stripe-connect.svg"
												alt="Stripe Integration"
											/>
											<Typography as="h3" $variant="title-5-semibold">
												{t(
													"createEventTicketModal.paidTicket.stripeNotEnabled.title",
												)}
											</Typography>
											<Typography as="p" $variant="body-2-regular">
												{t(
													"createEventTicketModal.paidTicket.stripeNotEnabled.description",
												)}
											</Typography>
											<a
												href={"/user/" + userId + "/settings"}
												target="_blank"
												rel="noopener noreferrer"
											>
												<Button size="small">
													{t(
														"createEventTicketModal.paidTicket.stripeNotEnabled.button",
													)}
												</Button>
											</a>
										</ConnectedAccountBanner>
									</ConnectedAccountWrapper>
								)}

								{/* Caso 3: El usuario tiene la cuenta y los cobros activados (se permite editar precio y capacidad) */}
								{hasStripeAccount && chargesEnabled && (
									<>
										<ModalContentRow>
											<InputWrapperHorizontal>
												<InputLabelWrapper>
													<Typography
														$variant="body-1-medium"
														as="label"
														color={theme.colors.defaultMain}
													>
														{t(
															"createEventTicketModal.paidTicket.enabled.capacityLabel",
														)}
													</Typography>
													<Tooltip
														text={t(
															"createEventTicketModal.freeTicket.capacityTooltip",
														)}
														maxWidth="220px"
														style={{ marginLeft: "-4px" }}
													>
														<InfoButton>
															<InfoCircleOutlineIcon
																width="20px"
																height="20px"
																fill={theme.colors.defaultSubtle}
															/>
														</InfoButton>
													</Tooltip>
												</InputLabelWrapper>

												<CapacityInput
													size="medium"
													type="number"
													value={localCapacity}
													onChange={(e) => setLocalCapacity(e.target.value)}
													placeholder={t(
														"createEventTicketModal.paidTicket.enabled.capacityPlaceholder",
													)}
													required
												/>
											</InputWrapperHorizontal>
										</ModalContentRow>

										<ModalContentRow>
											<InputWrapperHorizontal>
												<InputLabelWrapper>
													<Typography
														$variant="body-1-medium"
														as="label"
														color={theme.colors.defaultMain}
														// style={{ width: '100%' }}
													>
														{t(
															"createEventTicketModal.paidTicket.enabled.priceLabel",
														)}
													</Typography>
													<Tooltip
														text={t(
															"createEventTicketModal.freeTicket.capacityTooltip",
														)}
														maxWidth="220px"
														style={{ marginLeft: "-4px" }}
													>
														<InfoButton>
															<InfoCircleOutlineIcon
																width="20px"
																height="20px"
																fill={theme.colors.defaultSubtle}
															/>
														</InfoButton>
													</Tooltip>
												</InputLabelWrapper>
												<TicketPriceInput>
													<PriceInput
														size="medium"
														type="number"
														value={localPrice}
														onChange={(e) => setLocalPrice(e.target.value)}
														placeholder={t(
															"createEventTicketModal.paidTicket.enabled.pricePlaceholder",
														)}
														required
													/>
													<Typography>€</Typography>
												</TicketPriceInput>
											</InputWrapperHorizontal>
										</ModalContentRow>
									</>
								)}
							</>
						)}
					</>
				)}
			</ModalContent>
			<FormActions>
				<Button
					size="medium"
					onClick={() => {
						if (selectedTab === TICKET_PAID && !canUsePaid) return;
						const ticketObj = {
							name: localName,
							type: selectedTab,
							price: selectedTab === TICKET_PAID ? Number(localPrice) : 0,
							approvalRequired: !!localApprovalRequired,
							capacity: Number(localCapacity),
						};
						onSave?.(ticketObj);
						onClose();
					}}
					disabled={selectedTab === TICKET_PAID && !canUsePaid}
				>
					{t("createEventTicketModal.freeTicket.save")}
				</Button>
			</FormActions>

			{/* Modal para Onboarding de Stripe */}
			{showOnboardingModal && (
				<StripeOnboardingModal onClose={handleCloseOnboarding} />
			)}
		</Modal>
	);
};

export default CreateEventTicketModal;

// Estilos para el componente

const ModalContent = styled.div`
  align-items: flex-start;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-radius: ${({ theme }) => theme.radius.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  height: 100%;
  overflow-y: auto;
  padding: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const ModalContentRow = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ConnectedAccountWrapper = styled(ModalContentRow)`
  padding-bottom: ${({ theme }) => theme.sizing.sm}; 
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const TicketPriceInput = styled.div`
  display: flex;
  position: relative;

  span {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const FormActions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  text-align: center;
  width: 100%;
`;

const InputWrapperHorizontal = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 6px;
  width: 100%;
`;

const InputLabelWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs}; 
  width: 100%;
`;

const ConnectedAccountBanner = styled.div`
  align-items: flex-start;
  background: ${({ theme }) => theme.fill.defaultWeak};
  border-radius: ${({ theme }) => theme.sizing.xs};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.sm};
`;

const IntegrationIcon = styled.img`
  max-height: 40px;
  padding-bottom: ${({ theme }) => theme.sizing.xs};
  width: auto;
`;

const StripeActions = styled.div`
  padding: ${({ theme }) => theme.sizing.sm} 0 0 0;
`;

const InfoButton = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: ${({ theme }) => theme.sizing.md};
  justify-content: center;
  transition: background-color 0.3s ease-in-out;
  width: ${({ theme }) => theme.sizing.md};

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const CapacityInput = styled(InputText)`
  text-align: right;
  width: 120px;
`;

const PriceInput = styled(InputText)`
  padding-right: 24px;
  text-align: right;
  width: 120px;
`;
